import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import logo from '../assets/images/9works_Logo/svg/9works_Original_Black.svg';

function PrivacyPolicy() {
  useEffect(() => { window.scrollTo(0, 0); }, []);

  return (
    <div className="legal-page">
      <nav className="legal-nav">
        <div className="container">
          <div className="legal-nav-inner">
            <Link to="/" className="navbar-logo"><img src={logo} alt="9works" style={{ height: 36 }} /></Link>
            <Link to="/" className="btn btn-secondary" style={{ padding: '8px 20px', fontSize: 13 }}>Back to Home</Link>
          </div>
        </div>
      </nav>

      <div className="legal-content">
        <div className="container">
          <div className="legal-header">
            <h1>Privacy Policy</h1>
            <p className="legal-effective">Effective Date: March 25, 2026 &nbsp;|&nbsp; Last Updated: March 25, 2026</p>
          </div>

          <div className="legal-body">
            <p>
              This Privacy Policy describes how 9works, a product of ninea.ai ("we," "us," or "our"),
              collects, uses, shares, and protects your personal information when you use our website
              (<strong>9works.in</strong>), mobile applications, and related services (collectively, the "Service").
            </p>
            <p>
              By accessing or using our Service, you agree to the collection and use of information
              in accordance with this Privacy Policy. If you do not agree with this policy, please do not
              use our Service.
            </p>

            <h2>1. Information We Collect</h2>

            <h3>1.1 Information You Provide</h3>
            <ul>
              <li><strong>Account Information:</strong> Name, email address, phone number, job title, organization name, and password when you create an account.</li>
              <li><strong>Profile Information:</strong> Employee ID, department, role, skills, certifications, and availability preferences.</li>
              <li><strong>Contact Form Data:</strong> Name, email, company name, and message content submitted through our contact forms.</li>
              <li><strong>Billing Information:</strong> Company name, billing address, tax ID, and payment-related details for subscription management. We do not directly store credit card numbers.</li>
              <li><strong>Communication Data:</strong> Messages, feedback, support requests, and any other content you share with us.</li>
            </ul>

            <h3>1.2 Information Collected Automatically</h3>
            <ul>
              <li><strong>Usage Data:</strong> Pages visited, features used, click patterns, session duration, and interaction logs.</li>
              <li><strong>Device Information:</strong> Browser type, operating system, device type, screen resolution, and unique device identifiers.</li>
              <li><strong>Log Data:</strong> IP address, access times, referring URLs, and server response codes.</li>
              <li><strong>Location Data:</strong> Approximate location based on IP address. Precise geolocation only if you enable clock-in/clock-out geo-verification and grant explicit permission.</li>
              <li><strong>Cookies and Tracking:</strong> We use cookies, local storage, and similar technologies to maintain sessions, remember preferences, and analyze usage patterns.</li>
            </ul>

            <h3>1.3 Information from Third Parties</h3>
            <ul>
              <li><strong>Organization Administrators:</strong> Your employer or organization admin may provide your name, email, role, and department when onboarding you to the platform.</li>
              <li><strong>Authentication Providers:</strong> If you use single sign-on (SSO), we receive basic profile information from your identity provider.</li>
            </ul>

            <h2>2. How We Use Your Information</h2>
            <p>We use the collected information for the following purposes:</p>
            <ul>
              <li><strong>Service Delivery:</strong> To create and manage accounts, process shift schedules, track attendance, manage leave requests, and deliver core platform functionality.</li>
              <li><strong>Communication:</strong> To send notifications about shift changes, leave approvals, roster publications, system updates, and respond to your inquiries.</li>
              <li><strong>Billing & Subscriptions:</strong> To process payments, generate invoices, and manage subscription plans.</li>
              <li><strong>Analytics & Improvement:</strong> To understand usage patterns, improve our Service, fix bugs, and develop new features.</li>
              <li><strong>Security:</strong> To detect, prevent, and address fraud, unauthorized access, and other security issues.</li>
              <li><strong>Legal Compliance:</strong> To comply with applicable laws, regulations, and legal processes.</li>
              <li><strong>Customer Support:</strong> To provide technical support and resolve issues.</li>
            </ul>

            <h2>3. How We Share Your Information</h2>
            <p>We do not sell your personal information. We may share information in the following circumstances:</p>
            <ul>
              <li><strong>Within Your Organization:</strong> Your profile, schedule, attendance, and leave data are visible to authorized administrators and managers within your organization as necessary for workforce management.</li>
              <li><strong>Service Providers:</strong> We work with trusted third-party providers for hosting, email delivery, analytics, and payment processing. These providers are contractually bound to protect your data.</li>
              <li><strong>Platform Owner:</strong> Aggregated and anonymized organizational data may be visible to the platform owner for system administration purposes.</li>
              <li><strong>Legal Requirements:</strong> We may disclose information if required by law, regulation, legal process, or governmental request.</li>
              <li><strong>Business Transfers:</strong> In the event of a merger, acquisition, or sale of assets, your information may be transferred as part of that transaction.</li>
              <li><strong>With Your Consent:</strong> We may share information with your explicit consent for purposes not described in this policy.</li>
            </ul>

            <h2>4. Data Retention</h2>
            <p>
              We retain your personal information for as long as your account is active or as needed to provide
              the Service. When you or your organization administrator deletes an account, we will delete or
              anonymize the associated personal data within 90 days, except where retention is required by law
              or for legitimate business purposes (e.g., billing records, audit logs).
            </p>
            <p>
              Attendance records, shift logs, and leave history may be retained for the duration required by
              applicable labor laws and your organization's data retention policies.
            </p>

            <h2>5. Data Security</h2>
            <p>We implement industry-standard security measures to protect your information:</p>
            <ul>
              <li><strong>Encryption:</strong> Data in transit is protected using TLS/SSL encryption. Sensitive data at rest is encrypted using AES-256.</li>
              <li><strong>Access Controls:</strong> Role-based access controls (RBAC) ensure users only access data appropriate to their role.</li>
              <li><strong>Audit Logging:</strong> All access to sensitive data is logged and monitored.</li>
              <li><strong>Infrastructure:</strong> Our servers are hosted on secure, industry-certified infrastructure with regular security assessments.</li>
              <li><strong>Authentication:</strong> Passwords are hashed using bcrypt. We support multi-factor authentication (MFA).</li>
            </ul>
            <p>
              While we strive to protect your data, no method of electronic transmission or storage is 100%
              secure. We cannot guarantee absolute security.
            </p>

            <h2>6. Your Rights and Choices</h2>
            <p>Depending on your jurisdiction, you may have the following rights:</p>
            <ul>
              <li><strong>Access:</strong> Request a copy of the personal data we hold about you.</li>
              <li><strong>Correction:</strong> Request correction of inaccurate or incomplete data.</li>
              <li><strong>Deletion:</strong> Request deletion of your personal data, subject to legal and contractual obligations.</li>
              <li><strong>Data Portability:</strong> Request your data in a structured, machine-readable format.</li>
              <li><strong>Withdraw Consent:</strong> Withdraw consent for data processing where consent is the legal basis.</li>
              <li><strong>Opt-Out:</strong> Opt out of non-essential communications and marketing emails at any time.</li>
              <li><strong>Restrict Processing:</strong> Request restriction of processing in certain circumstances.</li>
            </ul>
            <p>
              To exercise any of these rights, contact us at <strong>privacy@ninea.ai</strong>. We will respond
              within 30 days or as required by applicable law.
            </p>

            <h2>7. Cookies and Tracking Technologies</h2>
            <p>We use the following types of cookies:</p>
            <ul>
              <li><strong>Essential Cookies:</strong> Required for the Service to function (authentication, session management). Cannot be disabled.</li>
              <li><strong>Analytics Cookies:</strong> Help us understand how users interact with the Service. You may opt out of these.</li>
              <li><strong>Preference Cookies:</strong> Remember your settings and preferences (language, timezone, theme).</li>
            </ul>
            <p>
              You can manage cookie preferences through your browser settings. Disabling essential cookies
              may impact the functionality of the Service.
            </p>

            <h2>8. Children's Privacy</h2>
            <p>
              Our Service is not directed to individuals under the age of 16. We do not knowingly collect
              personal information from children. If we become aware that we have collected personal data
              from a child without parental consent, we will take steps to delete that information.
            </p>

            <h2>9. International Data Transfers</h2>
            <p>
              Your information may be transferred to and processed in countries other than your country of
              residence. We ensure that appropriate safeguards are in place to protect your data in compliance
              with applicable data protection laws, including standard contractual clauses where required.
            </p>

            <h2>10. Third-Party Links</h2>
            <p>
              Our Service may contain links to third-party websites or services. We are not responsible for
              the privacy practices of these third parties. We encourage you to review their privacy policies
              before providing any personal information.
            </p>

            <h2>11. Changes to This Privacy Policy</h2>
            <p>
              We may update this Privacy Policy from time to time. We will notify you of any material changes
              by posting the new policy on this page and updating the "Last Updated" date. For significant
              changes, we may also notify you via email or in-app notification. Your continued use of the
              Service after changes constitutes acceptance of the updated policy.
            </p>

            <h2>12. Contact Us</h2>
            <p>If you have any questions, concerns, or requests regarding this Privacy Policy, please contact us:</p>
            <div className="legal-contact-box">
              <p><strong>9works — A product by ninea.ai</strong></p>
              <p>Email: <a href="mailto:privacy@ninea.ai">privacy@ninea.ai</a></p>
              <p>Website: <a href="https://9works.in">9works.in</a></p>
              <p>Parent Company: <a href="https://www.ninea.ai" target="_blank" rel="noopener noreferrer">ninea.ai</a></p>
            </div>
          </div>
        </div>
      </div>

      <div className="legal-footer">
        <div className="container">
          <p>&copy; {new Date().getFullYear()} 9works. A product by <a href="https://www.ninea.ai" target="_blank" rel="noopener noreferrer">ninea.ai</a>. All rights reserved.</p>
        </div>
      </div>
    </div>
  );
}

export default PrivacyPolicy;
